import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import {
  Box,
  Typography,
  TextField,
  Button,
  Stack,
  Alert,
  CircularProgress,
  Breadcrumbs,
  Link,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { territoryMasterAPI, masterDataAPI } from "../services/api";
import Swal from "sweetalert2";
import $ from "jquery";
import "select2";
import "select2/dist/css/select2.min.css";
import "bootstrap/dist/css/bootstrap.min.css";
import "./RaiseComplaint.css";
import "./AddEditTerritoryMaster.css";
import { getRoutePermissions } from "../utils/auth";

const normalizeCountry = (item) => ({
  countryCode: item.countryCode || item.CountryCode || item.COUNTRYCODE || "",
  countryName: item.countryName || item.CountryName || item.COUNTRYNAME || "",
});

const normalizeRegion = (item) => ({
  regionCode: item.regionCode || item.RegionCode || item.REGIONCODE || "",
  regionName: item.regionName || item.RegionName || item.REGIONNAME || "",
  countryCode: item.countryCode || item.CountryCode || item.COUNTRYCODE || "",
});

const normalizeZone = (item) => ({
  zoneCode: item.zoneCode || item.ZoneCode || item.ZONECODE || "",
  zoneName: item.zoneName || item.ZoneName || item.ZONENAME || "",
  regionCode: item.regionCode || item.RegionCode || item.REGIONCODE || "",
});

const normalizeTerritory = (item) => ({
  territoryCode: item.territoryCode || item.TerritoryCode || item.TERRITORYCODE || "",
  territoryId: item.territoryId || item.TerritoryId || item.TERRITORYID || "",
  territoryName: item.territoryName || item.TerritoryName || item.TERRITORYNAME || "",
  countryCode: item.countryCode || item.CountryCode || item.COUNTRYCODE || "",
  regionCode: item.regionCode || item.RegionCode || item.REGIONCODE || "",
  zoneCode: item.zoneCode || item.ZoneCode || item.ZONECODE || "",
  isActive: item.isActive ?? item.IsActive ?? true,
});

const AddEditTerritoryMaster = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { territoryCode } = useParams();
  const isEditMode = !!territoryCode;

  const [territory, setTerritory] = useState({
    territoryCode: "",
    territoryId: "",
    territoryName: "",
    countryCode: "",
    regionCode: "",
    zoneCode: "",
    isActive: true,
  });

  const [dropdowns, setDropdowns] = useState({
    countries: [],
    regions: [],
    zones: [],
  });

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const countrySelectRef = useRef(null);
  const regionSelectRef = useRef(null);
  const zoneSelectRef = useRef(null);

  const currentUser = (() => {
    try {
      const u = localStorage.getItem("user");
      return u ? JSON.parse(u) : null;
    } catch (e) {
      return null;
    }
  })();

  // Get permissions for the parent route (/territories)
  const parentPath = "/territories";
  const routePermissions = getRoutePermissions(parentPath);
  const { canAdd, canEdit } = routePermissions;

  useEffect(() => {
    // Check permissions based on mode
    if (isEditMode && !canEdit) {
      Swal.fire({
        title: "Access Denied",
        text: "You do not have permission to edit territories.",
        icon: "error",
        confirmButtonText: "OK",
      }).then(() => {
        navigate("/territories");
      });
      return;
    } else if (!isEditMode && !canAdd) {
      Swal.fire({
        title: "Access Denied",
        text: "You do not have permission to add territories.",
        icon: "error",
        confirmButtonText: "OK",
      }).then(() => {
        navigate("/territories");
      });
      return;
    }

    loadDropdowns();
    if (isEditMode) {
      loadTerritory();
    } else {
      loadNextTerritoryCode();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [territoryCode, isEditMode, canAdd, canEdit]);

  // Initialize Select2 for Country dropdown
  useEffect(() => {
    if (dropdowns.countries.length === 0) {
      return;
    }

    if (countrySelectRef.current) {
      const $countrySelect = $(countrySelectRef.current);
      if ($countrySelect.data("select2")) {
        $countrySelect.select2("destroy");
      }
      $countrySelect.select2({
        width: "100%",
        placeholder: "Select Country",
        allowClear: false,
      });
      $countrySelect.on("select2:select", function (e) {
        const selectedCountryCode = e.params.data.id;
        handleChange("countryCode", selectedCountryCode);
        // Load zones for selected country
        loadZones(selectedCountryCode);
        // Reset dependent fields
        handleChange("zoneCode", "");
        handleChange("regionCode", "");
      });
      if (territory.countryCode) {
        $countrySelect.val(territory.countryCode).trigger("change");
      }
    }

    return () => {
      if (countrySelectRef.current) {
        const $select = $(countrySelectRef.current);
        if ($select.data("select2")) {
          $select.off("select2:select");
          $select.select2("destroy");
        }
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dropdowns.countries]);

  // Initialize Select2 for Zone dropdown
  useEffect(() => {
    if (dropdowns.zones.length === 0) {
      return;
    }

    if (zoneSelectRef.current) {
      const $zoneSelect = $(zoneSelectRef.current);
      if ($zoneSelect.data("select2")) {
        $zoneSelect.select2("destroy");
      }
      $zoneSelect.select2({
        width: "100%",
        placeholder: "Select Zone",
        allowClear: false,
      });
      $zoneSelect.on("select2:select", function (e) {
        const selectedZoneCode = e.params.data.id;
        handleChange("zoneCode", selectedZoneCode);
        // Load regions for selected zone
        loadRegions(territory.countryCode, selectedZoneCode);
        // Reset region
        handleChange("regionCode", "");
      });
      if (territory.zoneCode) {
        $zoneSelect.val(territory.zoneCode).trigger("change");
      }
    }

    return () => {
      if (zoneSelectRef.current) {
        const $select = $(zoneSelectRef.current);
        if ($select.data("select2")) {
          $select.off("select2:select");
          $select.select2("destroy");
        }
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dropdowns.zones]);

  // Initialize Select2 for Region dropdown
  useEffect(() => {
    if (dropdowns.regions.length === 0) {
      return;
    }

    if (regionSelectRef.current) {
      const $regionSelect = $(regionSelectRef.current);
      if ($regionSelect.data("select2")) {
        $regionSelect.select2("destroy");
      }
      $regionSelect.select2({
        width: "100%",
        placeholder: "Select Region",
        allowClear: false,
      });
      $regionSelect.on("select2:select", function (e) {
        handleChange("regionCode", e.params.data.id);
      });
      if (territory.regionCode) {
        $regionSelect.val(territory.regionCode).trigger("change");
      }
    }

    return () => {
      if (regionSelectRef.current) {
        const $select = $(regionSelectRef.current);
        if ($select.data("select2")) {
          $select.off("select2:select");
          $select.select2("destroy");
        }
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dropdowns.regions]);

  // Update Select2 values when territory changes
  useEffect(() => {
    if (countrySelectRef.current && dropdowns.countries.length > 0) {
      const $countrySelect = $(countrySelectRef.current);
      if ($countrySelect.data("select2")) {
        const currentVal = $countrySelect.val();
        if (currentVal !== (territory.countryCode || "")) {
          $countrySelect.val(territory.countryCode || "").trigger("change");
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [territory.countryCode]);

  useEffect(() => {
    if (zoneSelectRef.current && dropdowns.zones.length > 0) {
      const $zoneSelect = $(zoneSelectRef.current);
      if ($zoneSelect.data("select2")) {
        const currentVal = $zoneSelect.val();
        if (currentVal !== (territory.zoneCode || "")) {
          $zoneSelect.val(territory.zoneCode || "").trigger("change");
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [territory.zoneCode]);

  useEffect(() => {
    if (regionSelectRef.current && dropdowns.regions.length > 0) {
      const $regionSelect = $(regionSelectRef.current);
      if ($regionSelect.data("select2")) {
        const currentVal = $regionSelect.val();
        if (currentVal !== (territory.regionCode || "")) {
          $regionSelect.val(territory.regionCode || "").trigger("change");
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [territory.regionCode]);

  const loadDropdowns = async () => {
    try {
      setLoading(true);
      const countryRes = await masterDataAPI.getCountries();

      setDropdowns({
        countries: (countryRes?.data || []).map((c) => normalizeCountry(c)),
        regions: [],
        zones: [],
      });
    } catch (e) {
      console.error("Error loading dropdowns:", e);
      setError("Failed to load dropdown data. Please refresh the page.");
    } finally {
      setLoading(false);
    }
  };

  const loadZones = async (countryCode) => {
    try {
      if (!countryCode) {
        setDropdowns((prev) => ({ ...prev, zones: [] }));
        return;
      }

      const response = await territoryMasterAPI.getZones(countryCode);

      if (response?.success && response.data) {
        const items = response.data.zones || response.data.Zones || response.data || [];
        const activeZones = Array.isArray(items)
          ? items.filter(z => z.isActive !== false && z.IsActive !== false)
          : [];
        setDropdowns((prev) => ({
          ...prev,
          zones: activeZones.map((z) => normalizeZone(z)),
          regions: [], // Reset regions when zones change
        }));
      } else {
        setDropdowns((prev) => ({ ...prev, zones: [] }));
      }
    } catch (e) {
      console.error("Error loading zones:", e);
      setDropdowns((prev) => ({ ...prev, zones: [] }));
    }
  };

  const loadRegions = async (countryCode, zoneCode) => {
    try {
      if (!zoneCode) {
        setDropdowns((prev) => ({ ...prev, regions: [] }));
        return;
      }

      const response = await territoryMasterAPI.getRegions(countryCode, zoneCode);

      if (response?.success && response.data) {
        const items = response.data.regions || response.data.Regions || response.data || [];
        const activeRegions = Array.isArray(items)
          ? items.filter(r => r.isActive !== false && r.IsActive !== false)
          : [];
        setDropdowns((prev) => ({
          ...prev,
          regions: activeRegions.map((r) => normalizeRegion(r)),
        }));
      } else {
        setDropdowns((prev) => ({ ...prev, regions: [] }));
      }
    } catch (e) {
      console.error("Error loading regions:", e);
      setDropdowns((prev) => ({ ...prev, regions: [] }));
    }
  };

  const loadNextTerritoryCode = async () => {
    try {
      const response = await territoryMasterAPI.getNextCode();
      if (response && response.success && response.data) {
        setTerritory((prev) => ({ ...prev, territoryCode: response.data }));
      }
    } catch (e) {
      console.error("Error loading next territory code:", e);
      setTerritory((prev) => ({ ...prev, territoryCode: "TR00001" }));
    }
  };

  const loadTerritory = async () => {
    try {
      setLoading(true);
      const response = await territoryMasterAPI.getByCode(territoryCode);
      if (response && response.success && response.data) {
        const territoryData = normalizeTerritory(response.data);
        setTerritory(territoryData);
        // Load dependent dropdowns
        if (territoryData.countryCode) {
          await loadZones(territoryData.countryCode);
          if (territoryData.zoneCode) {
            await loadRegions(territoryData.countryCode, territoryData.zoneCode);
          }
        }
      } else {
        navigate("/territories");
      }
    } catch (e) {
      console.error("Error loading territory:", e);
      if (e.response?.status !== 404) {
        Swal.fire("Error", "Failed to load territory details", "error");
      }
      navigate("/territories");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setTerritory((prev) => ({ ...prev, [field]: value }));
  };

  const validateForm = () => {
    if (!territory.territoryCode?.trim()) {
      Swal.fire("Error", "Territory ID is required", "error");
      return false;
    }
    if (!territory.territoryName?.trim()) {
      Swal.fire("Error", "Territory Name is required", "error");
      return false;
    }
    if (!territory.countryCode) {
      Swal.fire("Error", "Country is required", "error");
      return false;
    }
    if (!territory.zoneCode) {
      Swal.fire("Error", "Zone is required", "error");
      return false;
    }
    if (!territory.regionCode) {
      Swal.fire("Error", "Region is required", "error");
      return false;
    }
    return true;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    setSaving(true);
    try {
      const createdBy = currentUser?.userIdCode || "SYSADMIN001";
      let response;

      if (isEditMode) {
        response = await territoryMasterAPI.update(
          territoryCode,
          territory,
          createdBy
        );
      } else {
        response = await territoryMasterAPI.create(territory, createdBy);
      }

      if (response && response.success) {
        Swal.fire({
          title: "Success",
          text: response.message || "Territory saved successfully",
          icon: "success",
          confirmButtonText: "OK",
        }).then(() => {
          navigate("/territories");
        });
      } else {
        Swal.fire(
          "Error",
          response.message || "Failed to save territory",
          "error"
        );
      }
    } catch (e) {
      console.error("Error saving territory:", e);
      Swal.fire(
        "Error",
        e.response?.data?.message ||
          "Failed to save territory. Please try again.",
        "error"
      );
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box className="add-edit-product-loading">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box className="add-edit-product-header">
        <Breadcrumbs
          aria-label="breadcrumb"
          className="add-edit-product-breadcrumb"
        >
          <Link
            underline="hover"
            color="inherit"
            href="/territories"
            onClick={(e) => {
              e.preventDefault();
              navigate("/territories");
            }}
            className="breadcrumb-link brdcrmbLstitms"
          >
            Territory Master
          </Link>
          <Typography
            color="text.primary"
            className="breadcrumb-current brdcrmbLstitms brdcrmbCurPg"
          >
            {isEditMode ? "Edit Territory" : "Add Territory"}
          </Typography>
        </Breadcrumbs>
        <Box className="add-edit-product-header-right">
          <Button
            className="add-edit-product-back-button"
            onClick={() => navigate("/territories")}
            startIcon={<ArrowBackIcon />}
          >
            Back
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert
          severity="error"
          className="add-edit-product-error-alert"
          onClose={() => setError(null)}
        >
          {error}
        </Alert>
      )}

      <div className="container-fluid add-edit-product-page">
        {/* Basic Information Section */}
        <div className="card-panel mb-3">
          <div className="row g-3 mb-4">
            <div className="col-12 col-md-6 col-lg-4">
              <div className="select2-wrapper">
                <label className="select2-label pnlLblsze">
                  Country <span style={{ color: "red" }}>*</span>
                </label>
                <select
                  ref={countrySelectRef}
                  className="form-control select2-country"
                  required
                >
                  <option value="">Select Country</option>
                  {dropdowns.countries.map((c) => (
                    <option key={c.countryCode} value={c.countryCode}>
                      {c.countryName}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="col-12 col-md-6 col-lg-4">
              <div className="select2-wrapper">
                <label className="select2-label pnlLblsze">
                  Zone <span style={{ color: "red" }}>*</span>
                </label>
                <select
                  ref={zoneSelectRef}
                  className="form-control select2-zone"
                  required
                  disabled={!territory.countryCode}
                >
                  <option value="">Select Zone</option>
                  {dropdowns.zones.map((z) => (
                    <option key={z.zoneCode} value={z.zoneCode}>
                      {z.zoneName}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="col-12 col-md-6 col-lg-4">
              <div className="select2-wrapper">
                <label className="select2-label pnlLblsze">
                  Region <span style={{ color: "red" }}>*</span>
                </label>
                <select
                  ref={regionSelectRef}
                  className="form-control select2-region"
                  required
                  disabled={!territory.zoneCode}
                >
                  <option value="">Select Region</option>
                  {dropdowns.regions.map((r) => (
                    <option key={r.regionCode} value={r.regionCode}>
                      {r.regionName}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="col-12 col-md-6 col-lg-4" style={{ display: "none" }}>
              <div className="textfield-wrapper">
                <label className="select2-label pnlLblsze">
                  Territory Code
                </label>
                <TextField
                  value={territory.territoryCode}
                  fullWidth
                  disabled={true}
                  size="small"
                  className="add-edit-product-textfield"
                />
              </div>
            </div>
            <div className="col-12 col-md-6 col-lg-4">
              <div className="textfield-wrapper">
                <label className="select2-label pnlLblsze">
                  Territory ID
                </label>
                <TextField
                  value={territory.territoryId || ""}
                  onChange={(e) => handleChange("territoryId", e.target.value)}
                  fullWidth
                  size="small"
                  placeholder="Enter Territory ID"
                  className="add-edit-product-textfield"
                />
              </div>
            </div>
            <div className="col-12 col-md-6 col-lg-4">
              <div className="textfield-wrapper">
                <label className="select2-label pnlLblsze">
                  Territory Name <span style={{ color: "red" }}>*</span>
                </label>
                <TextField
                  value={territory.territoryName}
                  onChange={(e) => handleChange("territoryName", e.target.value)}
                  required
                  fullWidth
                  size="small"
                  placeholder="Enter territory name"
                  className="add-edit-product-textfield"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="row mt-4">
          <div className="col-12">
            <Stack direction="row" spacing={2} justifyContent="flex-end">
              <Button
                variant="contained"
                onClick={() => navigate("/territories")}
                className="btn-cancel-product funbtns"
                startIcon={<CancelIcon />}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleSave}
                disabled={saving}
                startIcon={
                  saving ? <CircularProgress size={20} /> : <SaveIcon />
                }
                className="btn-save-product funbtns"
              >
                {saving ? "Saving..." : "Save"}
              </Button>
            </Stack>
          </div>
        </div>
      </div>
    </Box>
  );
};

export default AddEditTerritoryMaster;



